/**
 * Centralized modal back-button manager.
 * Maintains a stack of open modals with a single global popstate listener.
 * Only the topmost modal responds to the browser back button.
 * Prevents cascading closes when multiple modals/selects are open.
 *
 * Key fix: history.back() calls are DEFERRED via setTimeout so that if a new
 * modal registers in the same React render cycle, the back() is cancelled
 * instead of popping the new modal's freshly-pushed history entry.
 */

let stack = [];
let programmaticBackCount = 0;
let _nextId = 1;
const closedByPopstateIds = new Set();

// Lock body scroll when modals are open (prevents iOS scroll bleed-through)
function updateBodyScroll() {
  if (stack.length > 0) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
}

// Batching: defer history.back() so register() can cancel pending backs
let scheduledBacks = 0;
let flushTimer = null;

function flushHistoryBacks() {
  const count = scheduledBacks;
  scheduledBacks = 0;
  flushTimer = null;
  for (let i = 0; i < count; i++) {
    window.history.back();
  }
}

export function registerModal(onClose, label = '') {
  const id = _nextId++;
  stack.push({ id, onClose, label });

  if (scheduledBacks > 0) {
    // A previous unregister scheduled a back(). Reuse its history entry
    // instead of pushing new one (which the pending back would pop).
    scheduledBacks--;
    programmaticBackCount--;
  } else {
    window.history.pushState({ modalId: id }, '');
  }
  updateBodyScroll();
  return id;
}

export function unregisterModal(id) {
  const idx = stack.findIndex(m => m.id === id);
  if (idx === -1) return;
  stack.splice(idx, 1);
  updateBodyScroll();
  programmaticBackCount++;
  scheduledBacks++;

  if (!flushTimer) {
    flushTimer = setTimeout(flushHistoryBacks, 0);
  }
}

/**
 * Unregister a modal WITHOUT calling history.back().
 * Use when you want to close a modal and immediately replace the URL
 * (e.g., applying filters via setSearchParams with replace:true).
 * The caller is responsible for not leaving a stale history entry —
 * use setSearchParams(..., { replace: true }) to overwrite the modal's entry.
 */
export function unregisterModalSilent(id) {
  const idx = stack.findIndex(m => m.id === id);
  if (idx === -1) return;
  stack.splice(idx, 1);
  updateBodyScroll();
  // No history.back() — caller will replace the history entry
}

export function wasClosedByPopstate(id) {
  if (closedByPopstateIds.has(id)) {
    closedByPopstateIds.delete(id);
    return true;
  }
  return false;
}

/**
 * Returns true if there are modals in the stack (for back button handling).
 */
export function hasOpenModals() {
  return stack.length > 0;
}

// Single global popstate listener — only topmost modal responds
window.addEventListener('popstate', () => {
  if (programmaticBackCount > 0) {
    programmaticBackCount--;
    return;
  }
  if (stack.length > 0) {
    const top = stack.pop();
    closedByPopstateIds.add(top.id);
    updateBodyScroll();
    top.onClose();
  }
});
